import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

const BlogCardLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  height: 100%;
`;

const BlogCardExternalLink = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
  height: 100%;
`;

const BlogCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const BlogImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image ? `/images/${props.image}` : '/images/placeholder.jpg'});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  flex-shrink: 0;
`;

const BlogContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const BlogTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
  min-height: 3.2rem; /* Ensures consistent height for titles */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogExcerpt = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1rem;
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #eee;
`;

const BlogTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const BlogTag = styled.span`
  background-color: #f0f0f0;
  color: #666;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlogDate = styled.p`
  font-size: 0.85rem;
  color: #999;
  white-space: nowrap;
`;


const BlogPreview = ({ post }) => {
  const { language } = useLanguage();
  const isExternal = post.type !== 'internal';
  const linkUrl = isExternal ? post.externalUrl : `/blog/${post.slug}`;
  
  // Get the correct language version
  const title = typeof post.title === 'object' ? post.title[language] : post.title;
  const excerpt = typeof post.excerpt === 'object' ? post.excerpt[language] : post.excerpt;
  
  const cardContent = (
    <BlogCard>
      <BlogImage image={post.image} />
      <BlogContent>
        <BlogTitle>
          {title}
        </BlogTitle>
        <BlogExcerpt>{excerpt}</BlogExcerpt>
        <BlogMeta>
          <BlogTags>
            {post.tags.map((tag, index) => (
              <BlogTag key={index}>{tag}</BlogTag>
            ))}
          </BlogTags>
          <BlogDate>{new Date(post.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</BlogDate>
        </BlogMeta>
      </BlogContent>
    </BlogCard>
  );
  
  if (isExternal) {
    return (
      <BlogCardExternalLink href={linkUrl} target="_blank" rel="noopener noreferrer">
        {cardContent}
      </BlogCardExternalLink>
    );
  }
  
  return (
    <BlogCardLink to={linkUrl}>
      {cardContent}
    </BlogCardLink>
  );
};

export default BlogPreview; 